import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Star, StarOff, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FollowPoliticianButton
 * Props:
 *   politicianId: string
 *   politicianName: string
 */
export function FollowPoliticianButton({ politicianId, politicianName }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);

  // Check if already following
  useEffect(() => {
    if (!user) { setChecked(true); return; }
    axios
      .get(`${API}/user/profile`, { withCredentials: true })
      .then((r) => {
        setFollowing((r.data.followed_politicians || []).includes(politicianId));
        setChecked(true);
      })
      .catch(() => setChecked(true));
  }, [politicianId, user]);

  const handleClick = async () => {
    if (!user) {
      navigate("/auth?redirect=/politicians/" + politicianId);
      return;
    }
    setLoading(true);
    try {
      if (following) {
        await axios.delete(`${API}/user/follow/politician/${politicianId}`, { withCredentials: true });
        setFollowing(false);
      } else {
        await axios.post(`${API}/user/follow/politician/${politicianId}`, {}, { withCredentials: true });
        setFollowing(true);
      }
    } catch {}
    setLoading(false);
  };

  if (!checked) return null;

  if (!user) {
    return (
      <button
        onClick={handleClick}
        className="flex items-center gap-1.5 text-xs font-black px-3 py-2 border-2 border-white hover:-translate-y-0.5 transition-transform"
        style={{ backgroundColor: "rgba(255,255,255,0.1)", color: "#fff" }}
        data-testid="follow-politician-login-btn"
        title="Sign in to follow"
      >
        <Lock className="w-3 h-3" /> Follow
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className="flex items-center gap-1.5 text-xs font-black px-3 py-2 border-2 border-white hover:-translate-y-0.5 transition-transform disabled:opacity-60"
      style={{
        backgroundColor: following ? "#FFC82E" : "rgba(255,255,255,0.1)",
        color: following ? "#000" : "#fff",
        boxShadow: following ? "2px 2px 0 rgba(255,255,255,0.3)" : "none",
      }}
      data-testid={following ? "unfollow-politician-btn" : "follow-politician-btn"}
    >
      {following ? (
        <><StarOff className="w-3 h-3" /> {loading ? "…" : "Unfollow"}</>
      ) : (
        <><Star className="w-3 h-3" /> {loading ? "…" : "Follow"}</>
      )}
    </button>
  );
}
